import React from "react";
import { Post } from "@/core/types/postTypes";
import { MediaDisplay } from "@/components/MediaCard/MediaDisplay";
import { VideoPlayer } from "@/components/ui/VideoPlayer";
import { PostBadge } from "@/components/PostCard";
import { usePostMedia } from "./usePostMedia";

interface PostMediaThumbnailProps {
  post: Post;
  /** Tailwind class controlling outer width. Default mirrors ListPostCard. */
  widthClass?: string;
  /** Tailwind class controlling aspect ratio. Default 4:5 — social proportions. */
  aspectClass?: string;
  /** Rounded corner class. */
  roundedClass?: string;
  /** Show the floating carousel-count chip on the top-right corner. */
  showCarouselCount?: boolean;
  /** Show the hover-only reel play indicator. */
  showReelHoverOverlay?: boolean;
  /** Additional classes on the inner thumb wrapper. */
  innerClassName?: string;
  /** Floating slot positioned at the bottom-left corner of the thumb. */
  bottomLeftOverlay?: React.ReactNode;
}

/**
 * Shared post-thumbnail primitive used by list/row layouts (ListPostCard,
 * ads candidate row, etc.). Wraps `usePostMedia` so carousel cycling,
 * reel video playback, and the "no media" fallback behave the same way
 * everywhere.
 */
export const PostMediaThumbnail: React.FC<PostMediaThumbnailProps> = ({
  post,
  widthClass = "w-24",
  aspectClass = "aspect-4/5",
  roundedClass = "rounded-xl",
  showCarouselCount = true,
  showReelHoverOverlay = true,
  innerClassName = "",
  bottomLeftOverlay,
}) => {
  const {
    currentImage,
    imageCount,
    currentImageIndex,
    getReelVideoItem,
    handleImageClick,
    handleVideoClick,
    handleVideoPlayerClose,
    isVideoPlayerOpen,
    selectedVideoForPlayer,
    getStreamUrlByFileId,
    authContext,
    selectedClient,
  } = usePostMedia(post);

  const isReel = post.postType === "reel";
  const isInteractive = isReel || (post.isCarousel && imageCount > 1);

  return (
    <div className="relative shrink-0">
      <div
        onClick={(e) => {
          if (isReel) {
            e.stopPropagation();
            const videoItem = getReelVideoItem();
            if (videoItem) handleVideoClick(videoItem);
          } else {
            handleImageClick(e);
          }
        }}
        className={[
          "group/thumb relative overflow-hidden",
          widthClass,
          aspectClass,
          roundedClass,
          "ring-1 ring-gray-200/80",
          "shadow-[0_2px_6px_-3px_rgb(0_0_0/0.12)]",
          isInteractive
            ? "cursor-pointer hover:ring-gray-300 hover:scale-[1.02] transition-all duration-200"
            : "",
          innerClassName,
        ].join(" ")}
        title={
          isReel
            ? "Click to play reel"
            : post.isCarousel && imageCount > 1
              ? `Click to cycle (${currentImageIndex + 1}/${imageCount})`
              : undefined
        }
      >
        {currentImage ? (
          <div className="absolute inset-0">
            <MediaDisplay
              mediaItem={currentImage}
              useThumbnail={true}
              showPlayButton={false}
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-gray-300 text-[10px]">
            No media
          </div>
        )}

        {isReel && showReelHoverOverlay && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/15 transition-colors"
          >
            <span className="w-7 h-7 rounded-full bg-white/85 shadow-md flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity">
              <svg
                className="w-3.5 h-3.5 text-gray-900 ml-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        )}
      </div>

      {!isReel && showCarouselCount && post.isCarousel && imageCount > 1 && (
        <div className="absolute -top-1.5 -right-1.5">
          <PostBadge.CarouselCountBadge
            currentIndex={currentImageIndex}
            totalItems={imageCount}
          />
        </div>
      )}

      {bottomLeftOverlay && (
        <div className="absolute -bottom-1.5 -left-1.5">{bottomLeftOverlay}</div>
      )}

      {selectedVideoForPlayer && (
        <VideoPlayer
          mediaItem={selectedVideoForPlayer}
          isOpen={isVideoPlayerOpen}
          onClose={handleVideoPlayerClose}
          getStreamUrlByFileId={getStreamUrlByFileId}
          clientId={authContext?.clientId || selectedClient?.id}
        />
      )}
    </div>
  );
};
